import { useRef } from 'react';

import {
  FormFieldsProvider,
  MultiStepDialogMui as MultiStepDialog,
  MultiStepDialogContentHandlers,
  useFormField,
} from '@spotify/confidence-core-react';
import {
  AboutStep,
  MeasurementFormValues,
  TypeStep,
  useDeriveMeasurementNameExtension,
} from '@spotify/plugin-confidence-metrics-react';

import {
  AggregationStep,
  InputsStep,
  MissingValuesStep,
  SummaryStep,
} from './steps';
import { useCreateMeasurementDialog } from './useCreateMeasurementDialog';

type MultiStepDialogContentProps = {
  step: number;
  onStepChange: (step: number) => void;
  onClose: () => void;
  onSubmit: (e?: React.FormEvent) => void;
  loading?: boolean;
};

const MultiStepDialogContent = ({
  step,
  onStepChange,
  onClose,
  onSubmit,
  loading,
}: MultiStepDialogContentProps) => {
  const contentRef = useRef<MultiStepDialogContentHandlers>(null);
  const deriveExtension = useDeriveMeasurementNameExtension();

  const { value: metricType } = useFormField<
    MeasurementFormValues,
    'metricType'
  >('metricType');

  const isAverageType =
    metricType === 'average' ||
    metricType === 'conversion' ||
    metricType === 'consumption';
  const isRatioType = metricType === 'ratio' || metricType === 'ctr';
  const showAggregation = isAverageType || isRatioType;

  const goToStep = (stepId: string) => {
    contentRef.current?.go(stepId);
  };

  return (
    <MultiStepDialog.Root open onClose={onClose} fullScreen maxWidth="md">
      <MultiStepDialog.Content
        ref={contentRef}
        step={step}
        onStepChange={onStepChange}
        component="form"
        onSubmit={onSubmit}
      >
        <MultiStepDialog.Progress />

        <TypeStep resourceType="measurement" />
        <InputsStep />
        {showAggregation && <AggregationStep />}
        <MissingValuesStep />
        {!deriveExtension && <AboutStep resourceType="measurement" />}
        <SummaryStep loading={loading} goToStep={goToStep} />
      </MultiStepDialog.Content>
    </MultiStepDialog.Root>
  );
};

export const CreateMeasurementDialog = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const { step, setStep, formFieldsRef, initialValues, handleSubmit, loading } =
    useCreateMeasurementDialog();

  return (
    <FormFieldsProvider<MeasurementFormValues>
      ref={formFieldsRef}
      initialValues={initialValues as MeasurementFormValues}
    >
      <MultiStepDialogContent
        step={step}
        onStepChange={setStep}
        onClose={onClose}
        onSubmit={handleSubmit}
        loading={loading}
      />
    </FormFieldsProvider>
  );
};
